import { useTranslation } from "react-i18next";
import { useSnackbar } from "src/notifications/SnackbarProvider.ts";
import { useMemo, useState } from "react";
import type { GridFilterModel } from "@mui/x-data-grid-premium";
import { useDebouncedValue } from "@exonn/hooks";
import type { TransferListProps as BaseTransferListProps } from "src/components/TransferList/TransferList.tsx";
import { SelectV2 } from "src/components/SelectV2/SelectV2.tsx";
import { AssignRecordsView } from "src/components/Form/panels/TransferPanel/views/AssignRecordsView/AssignRecordsView.tsx";
import type {
  TransferPanelManageData,
  ValidRowModel,
} from "src/components/Form/panels/TransferPanel/TransferPanel.tsx";
import { TranslationNamespaceEnum } from "src/lib/i18next/types.ts";
import { ResourceEnum } from "src/constants/constants.ts";
import { useParams } from "react-router-dom";
import { useResourceFormLang } from "src/components/Form/hooks/useResourceFormLang.ts";
import { useResourceFormApi } from "src/components/Form/hooks/useResourceFormApi.ts";
import { useTransferListRecords } from "src/components/Form/panels/TransferPanel/hooks/useTransferListRecords.ts";
import { useProductGroups } from "src/components/Form/panels/TransferPanel/hooks/useProductGroups.ts";
import { useManageTransferListRecords } from "src/components/Form/panels/TransferPanel/hooks/useManageTransferListRecords.ts";
import { useTransferListDataGridColumns } from "src/components/Form/panels/TransferPanel/hooks/useTransferListDataGridColumns.tsx";

const emptyFilterModel = {
  items: [],
};

export type TransferListProps = {
  onClose?: () => void;
  resource: string;
  formResource: string;
  isExcluded: boolean;
};

export const TransferList = <Row extends ValidRowModel = ValidRowModel>(
  props: TransferListProps,
) => {
  const { onClose, isExcluded, resource, formResource } = props;

  const formApi = useResourceFormApi();

  const { t } = useTranslation(TranslationNamespaceEnum.TransferPanel);
  const snackbar = useSnackbar();
  const params = useParams() as { id: string };

  const [filterModelLeft, setFilterModelLeft] =
    useState<GridFilterModel>(emptyFilterModel);
  const [filterModelRight, setFilterModelRight] =
    useState<GridFilterModel>(emptyFilterModel);

  const [appliedFilterModelLeft, setAppliedFilterModelLeft] =
    useState<GridFilterModel>(emptyFilterModel);
  const [appliedFilterModelRight, setAppliedFilterModelRight] =
    useState<GridFilterModel>(emptyFilterModel);

  const [productGroupIdLeft, setProductGroupIdLeft] = useState<
    string | undefined
  >(undefined);

  const [productGroupIdRight, setProductGroupIdRight] = useState<
    string | undefined
  >(undefined);

  const [searchedCategoryLeft, setSearchedCategoryLeft] = useState("");
  const [searchedCategoryRight, setSearchedCategoryRight] = useState("");

  const [debouncedSearchedCategoryLeft] = useDebouncedValue(
    searchedCategoryLeft,
    500,
  );
  const [debouncedSearchedCategoryRight] = useDebouncedValue(
    searchedCategoryRight,
    500,
  );

  const locale = useResourceFormLang();

  const leftProductGroupInfiniteSelect = useProductGroups({
    resource,
    search: debouncedSearchedCategoryLeft,
  });

  const rightProductGroupInfiniteSelect = useProductGroups({
    resource,
    search: debouncedSearchedCategoryRight,
  });

  const manageRecordsMutation = useManageTransferListRecords({
    formResource,
    resource,
    params,
    mutationOptions: {
      onSuccess: () => {
        notAssignedRowsQuery.refetch();
        assignedRowsQuery.refetch();
        formApi.refresh({
          resetForm: false,
          revalidateData: true,
        });
      },
    },
  });

  const combinedAppliedFilterModelLeft = useMemo(
    () => ({
      ...appliedFilterModelLeft,
      items: [
        ...appliedFilterModelLeft.items,
        ...(productGroupIdLeft
          ? [
              {
                operator: "exact",
                field: "product_group_id",
                value: productGroupIdLeft,
              },
            ]
          : []),
      ],
    }),
    [productGroupIdLeft, appliedFilterModelLeft],
  );

  const notAssignedRowsQuery = useTransferListRecords({
    is_assigned: 0,
    is_excluded: isExcluded ? 1 : 0,
    filter: combinedAppliedFilterModelLeft,
    resource,
    formResource,
    params,
    locale,
  });

  const combinedAppliedFilterModelRight = useMemo(
    () => ({
      ...appliedFilterModelRight,
      items: [
        ...appliedFilterModelRight.items,
        ...(productGroupIdRight
          ? [
              {
                operator: "exact",
                field: "product_group_id",
                value: productGroupIdRight,
              },
            ]
          : []),
      ],
    }),
    [productGroupIdRight, appliedFilterModelRight],
  );

  const transferListDataGridColumns = useTransferListDataGridColumns({
    resource,
    formResource,
    t,
  });

  const assignedRowsQuery = useTransferListRecords({
    is_assigned: 1,
    is_excluded: isExcluded ? 1 : 0,

    filter: combinedAppliedFilterModelRight,
    resource,
    formResource,
    params,
    locale,
  });

  const allRowsIncluded = useMemo(() => {
    return (notAssignedRowsQuery.data?.pages || []).reduce(
      (acc, page) => [...acc, ...page.data],
      [] as Row[],
    );
  }, [notAssignedRowsQuery.data]);

  const includedRows = useMemo(() => {
    return (assignedRowsQuery.data?.pages || []).reduce(
      (acc, page) => [...acc, ...page.data],
      [] as Row[],
    );
  }, [assignedRowsQuery.data]);

  const runManageMutation = (
    action: TransferPanelManageData["action"],
    options?: { rows?: Row[]; filter?: GridFilterModel },
  ) => {
    manageRecordsMutation.mutate(
      {
        data: {
          action,
          rows: options?.rows,
          filter: options?.filter,
          is_excluded: isExcluded ? 1 : 0,
          locale,
        },
      },
      {
        onSuccess: () =>
          snackbar.success({ message: t(`success.${action}`), flash: true }),
        onError: () =>
          snackbar.error({ message: t(`error.${action}`), flash: true }),
      },
    );
  };

  const handleAssign = (rows: Row[]) => runManageMutation("assign", { rows });

  const handleUnassign = (rows: Row[]) =>
    runManageMutation("unassign", { rows });

  const handleAssignAll = () =>
    runManageMutation("assignAll", { filter: combinedAppliedFilterModelLeft });

  const handleUnassignAll = () =>
    runManageMutation("unassignAll", {
      filter: combinedAppliedFilterModelRight,
    });

  const leftTableProps: BaseTransferListProps<Row>["dataGridPropsLeft"] = {
    title: t(`${resource}.include.transfer-list.left.title`),
    hideToolbar: true,
    error: notAssignedRowsQuery.error,
    onRecoverFromError: () => {
      setFilterModelLeft(emptyFilterModel);
      notAssignedRowsQuery.refetch();
    },
    renderSubTitle: () => {
      if (resource === ResourceEnum.Variant) {
        return (
          <SelectV2
            label={t("include.transferList.productGroupFilter.label")}
            isLoadingData={leftProductGroupInfiniteSelect.query.isFetching}
            style={{
              marginBottom: 10,
            }}
            onOpenChange={(isOpen) => {
              if (isOpen) {
                leftProductGroupInfiniteSelect.query.refetch();
              }
            }}
            isLoadingMoreOptions={
              leftProductGroupInfiniteSelect.query.isFetchingNextPage
            }
            onScrollEnd={() => {
              if (!leftProductGroupInfiniteSelect.query.isFetchingNextPage) {
                leftProductGroupInfiniteSelect.query.fetchNextPage();
              }
            }}
            onChange={(value) => setProductGroupIdLeft(value)}
            value={productGroupIdLeft}
            showSearch
            searchValue={searchedCategoryLeft}
            options={leftProductGroupInfiniteSelect.options}
            onSearchValueChange={setSearchedCategoryLeft}
          />
        );
      }
      return null;
    },
    rows: allRowsIncluded,
    rowCount: notAssignedRowsQuery.data?.pages?.[0]?.meta?.total || 0,
    loading: notAssignedRowsQuery.isFetching || manageRecordsMutation.isPending,
    columns: transferListDataGridColumns,

    filterModel: filterModelLeft,
    onFilterModelChange: setFilterModelLeft,
    appliedFilterModel: appliedFilterModelLeft,
    onAppliedFilterModelChange: setAppliedFilterModelLeft,
    filterMode: "server",
    sortingMode: "server",
    paginationMode: "server",
    onRowsScrollEnd: () => {
      if (
        notAssignedRowsQuery.hasNextPage &&
        !notAssignedRowsQuery.isFetching
      ) {
        notAssignedRowsQuery.fetchNextPage();
      }
    },
  };

  const rightTableProps: BaseTransferListProps<Row>["dataGridPropsRight"] = {
    title: t(`${resource}.include.transfer-list.right.title`),
    error: assignedRowsQuery.error,
    hideToolbar: true,
    onRecoverFromError: () => {
      setFilterModelRight(emptyFilterModel);
      assignedRowsQuery.refetch();
    },
    renderSubTitle: () => {
      if (resource === ResourceEnum.Variant) {
        return (
          <SelectV2
            label={t("include.transferList.productGroupFilter.label")}
            isLoadingData={rightProductGroupInfiniteSelect.query.isFetching}
            style={{
              marginBottom: 10,
            }}
            onOpenChange={(isOpen) => {
              if (isOpen) {
                rightProductGroupInfiniteSelect.query.refetch();
              }
            }}
            isLoadingMoreOptions={
              rightProductGroupInfiniteSelect.query.isFetchingNextPage
            }
            onScrollEnd={() => {
              if (!rightProductGroupInfiniteSelect.query.isFetchingNextPage) {
                rightProductGroupInfiniteSelect.query.fetchNextPage();
              }
            }}
            onChange={(value) => setProductGroupIdRight(value)}
            value={productGroupIdRight}
            showSearch
            searchValue={searchedCategoryRight}
            options={rightProductGroupInfiniteSelect.options}
            onSearchValueChange={setSearchedCategoryRight}
          />
        );
      }
      return null;
    },
    rows: includedRows,
    rowCount: assignedRowsQuery.data?.pages?.[0]?.meta?.total || 0,
    loading: assignedRowsQuery.isFetching || manageRecordsMutation.isPending,
    columns: transferListDataGridColumns,
    filterModel: filterModelRight,
    onFilterModelChange: setFilterModelRight,
    appliedFilterModel: appliedFilterModelRight,
    onAppliedFilterModelChange: setAppliedFilterModelRight,
    filterMode: "server",
    sortingMode: "server",
    paginationMode: "server",
    onRowsScrollEnd: () => {
      if (assignedRowsQuery.hasNextPage && !assignedRowsQuery.isFetching) {
        assignedRowsQuery.fetchNextPage();
      }
    },
  };

  return (
    <AssignRecordsView
      onTransferAllToRight={handleAssignAll}
      onTransferAllToLeft={handleUnassignAll}
      onTransferSelectedToRight={handleAssign}
      onTransferSelectedToLeft={handleUnassign}
      dataGridPropsLeft={leftTableProps}
      dataGridPropsRight={rightTableProps}
      onClose={onClose}
    />
  );
};
